import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudies } from '../../hooks/useStudies';
import { Study, StudyStatus, StudyType } from '../../types/study.types';

const StudyList: React.FC = () => {
  const navigate = useNavigate();
  const { studies, loading, error } = useStudies();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StudyStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<StudyType | 'all'>('all');

  const filteredStudies = studies.filter((study) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      study.title.toLowerCase().includes(searchLower) ||
      study.codeSettings.prefix.toLowerCase().includes(searchLower) ||
      study.description?.toLowerCase().includes(searchLower);

    const matchesStatus = statusFilter === 'all' || study.status === statusFilter;
    const matchesType = typeFilter === 'all' || study.type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadgeColor = (status: StudyStatus) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'archived':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeBadgeColor = (type: StudyType) => {
    return type === 'rct' ? 'bg-purple-100 text-purple-800' : 'bg-teal-100 text-teal-800';
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getEnrollmentStats = (study: Study) => {
    const enrolled = study.analytics?.totalEnrolled || 0;
    const target = study.enrollmentSettings?.targetSampleSize || 0;
    const percentage = target > 0 ? Math.round((enrolled / target) * 100) : 0;
    return { enrolled, target, percentage };
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Observational Studies</h1>
        <p className="text-sm text-gray-600">Manage observational studies and participant enrollment</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {/* Actions Bar */}
      <div className="mb-4 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <input
            type="text"
            placeholder="Search studies..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StudyStatus | 'all')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as StudyType | 'all')}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="observational">Observational</option>
            <option value="rct">RCT</option>
          </select>
        </div>

        {/* Create Study Button */}
        <button
          onClick={() => navigate('/studies/create')}
          className="w-full sm:w-auto px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          + Create New Study
        </button>
      </div>

      {/* Studies Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-3 text-sm text-gray-600">Loading studies...</p>
          </div>
        ) : filteredStudies.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">
            {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
              ? 'No studies found matching your filters.'
              : 'No studies yet. Create your first study to get started.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Study
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Enrollment
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Timepoints
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudies.map((study) => {
                  const stats = getEnrollmentStats(study);
                  return (
                    <tr
                      key={study.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/studies/${study.id}`)}
                    >
                      {/* Study Info */}
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-8 w-8 bg-teal-100 rounded-full flex items-center justify-center">
                            <span className="text-teal-600 font-medium text-xs">
                              {study.codeSettings.prefix.substring(0, 2).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{study.title}</div>
                            <div className="text-xs text-gray-500">{study.codeSettings.prefix}</div>
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getTypeBadgeColor(
                            study.type
                          )}`}
                        >
                          {study.type === 'rct' ? 'RCT' : 'Observational'}
                        </span>
                      </td>

                      {/* Status */}
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full ${getStatusBadgeColor(
                            study.status
                          )}`}
                        >
                          {study.status.charAt(0).toUpperCase() + study.status.slice(1)}
                        </span>
                        {!study.enrollmentSettings?.isOpen && study.status === 'active' && (
                          <span className="ml-2 px-2 py-0.5 inline-flex text-xs leading-4 font-semibold rounded-full bg-orange-100 text-orange-800">
                            Enrollment Closed
                          </span>
                        )}
                      </td>

                      {/* Enrollment */}
                      <td className="px-4 py-2.5 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-1 w-24">
                            <div className="text-sm text-gray-900">
                              {stats.enrolled} / {stats.target}
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
                              <div
                                className="bg-teal-600 h-1.5 rounded-full"
                                style={{ width: `${Math.min(stats.percentage, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                          <span className="ml-2 text-xs text-gray-500">{stats.percentage}%</span>
                        </div>
                      </td>

                      {/* Timepoints */}
                      <td className="px-4 py-2.5 whitespace-nowrap text-sm text-gray-900">
                        {study.surveySchedule?.length || 0}
                      </td>

                      {/* Created Date */}
                      <td className="px-4 py-2.5 whitespace-nowrap text-xs text-gray-500">
                        {formatDate(study.createdAt)}
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-2.5 whitespace-nowrap text-right text-xs font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/studies/${study.id}`);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Stats Summary */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Total Studies</p>
          <p className="text-xl font-bold text-gray-900 mt-0.5">{studies.length}</p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Active</p>
          <p className="text-xl font-bold text-green-600 mt-0.5">
            {studies.filter((s) => s.status === 'active').length}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Total Enrolled</p>
          <p className="text-xl font-bold text-teal-600 mt-0.5">
            {studies.reduce((sum, s) => sum + (s.analytics?.totalEnrolled || 0), 0)}
          </p>
        </div>
        <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500 text-xs font-semibold uppercase tracking-wide">Completed</p>
          <p className="text-xl font-bold text-blue-600 mt-0.5">
            {studies.filter((s) => s.status === 'completed').length}
          </p>
        </div>
      </div>
    </div>
  );
};

export default StudyList;
